import DeleteIcon from "@mui/icons-material/Delete";
import { Theme } from "@mui/system";
import { memo } from "react";
import { When } from "react-if";

import { Box, IconButton } from "@tsp/tsp-frontend-components/mui";

import { AttachmentType } from "../../attachment-field.enums";
import { useAttachmentMeta } from "../../attachment-field.hooks";
import { AttachmentValue } from "../../attachment-field.types";

import ExtensionBadge from "../extension-badge";

const classes = {
  root: (link: boolean) => ({
    borderRadius: 1,
    overflow: "hidden",
    width: "128px",
    height: "89px",
    position: "relative",
    backgroundColor: "#F0F0F0",
    cursor: link ? "pointer" : "default",
  }),
  name: (theme: Theme) => ({
    lineHeight: 1.3333,
    fontSize: "0.857rem",
    wordBreak: "break-word",
    color: theme.palette.text.primary,
  }),
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  main: (theme: Theme) => ({
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    position: "absolute",
    boxSizing: "border-box",
    flexDirection: "column",
    padding: theme.spacing(1),
    justifyContent: "space-between",
  }),
  remove: (theme: Theme) => ({
    borderRadius: 1,
    cursor: "pointer",
    fontSize: "1.125rem",
    lineHeight: 0,
    display: "inline-block",
    backgroundColor: "#fff",
    color: theme.palette.primary.main,
    padding: `${theme.spacing(0.5)}`,

    "&:hover": {
      backgroundColor: "#fff",
    },
  }),
  image: {
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    position: "absolute",
  },
};

type ThumbnailProps = {
  value: AttachmentValue;
  disabled: boolean;
  onRemove: () => void;
};

function Thumbnail(props: ThumbnailProps) {
  const { value, disabled, onRemove } = props;

  const meta = useAttachmentMeta(value);
  const link = value.type !== AttachmentType.File;

  const extensionComponent = meta.extension ? <ExtensionBadge extension={meta.extension} /> : <div />;

  return (
    <Box sx={classes.root(link)} data-test-id={props["data-test-id"]}>
      <When condition={!!meta.preview}>
        <Box component="img" src={meta.preview} alt="" sx={classes.image} />
      </When>
      <Box sx={classes.main}>
        <Box sx={classes.header}>
          {extensionComponent}
          <IconButton
            sx={classes.remove}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            disabled={disabled}
          >
            <DeleteIcon fontSize="inherit" color="inherit" />
          </IconButton>
        </Box>
        <When condition={!!meta.title}>
          <Box sx={classes.name}>{meta.title}</Box>
        </When>
      </Box>
    </Box>
  );
}

export default memo(Thumbnail);
